import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { ArrowLeft, ShoppingBag, FileText, BookOpen, GraduationCap, ExternalLink } from 'lucide-react';

const Purchases = () => {
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPurchases();
  }, []);

  const fetchPurchases = async () => {
    try {
      const response = await axios.get(`${API}/purchases`);
      setPurchases(response.data);
    } catch (error) {
      toast.error('Failed to load purchases');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Button
          data-testid="back-btn"
          onClick={() => navigate('/')}
          variant="ghost"
          className="mb-6 rounded-xl"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Marketplace
        </Button>

        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 border border-gray-200">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl flex items-center justify-center shadow-lg">
              <GraduationCap className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800" style={{ fontFamily: 'Crimson Text, serif' }}>
                My Purchases
              </h1>
              <p className="text-gray-500 text-sm">Access your purchased notes and study materials</p>
            </div>
          </div>

          <div className="grid gap-4">
            {purchases.map((purchase) => (
              <div
                key={purchase.id}
                data-testid={`purchase-${purchase.id}`}
                className="p-5 bg-gradient-to-r from-gray-50 to-blue-50/30 rounded-xl border border-gray-200 hover:shadow-lg transition-all"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <FileText className="w-6 h-6 text-blue-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-800 mb-1 truncate" style={{ fontFamily: 'Crimson Text, serif' }}>
                        {purchase.document_title}
                      </h3>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                        <span>Purchased {new Date(purchase.purchase_date).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 ml-16 md:ml-0">
                    <div className="text-right mr-2">
                      <p className="text-xl font-bold text-gray-700" style={{ fontFamily: 'Crimson Text, serif' }}>
                        ${purchase.price.toFixed(2)}
                      </p>
                    </div>
                    <Button
                      data-testid={`view-purchase-${purchase.id}`}
                      onClick={() => navigate(`/document/${purchase.document_id}`)}
                      variant="outline"
                      className="rounded-xl"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      View
                    </Button>
                    <Button
                      data-testid={`study-purchase-${purchase.id}`}
                      onClick={() => navigate(`/study/${purchase.document_id}`)}
                      className="rounded-xl bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 shadow-md"
                    >
                      <BookOpen className="w-4 h-4 mr-2" />
                      Study
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {purchases.length === 0 && (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShoppingBag className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No purchases yet</h3>
              <p className="text-gray-500 mb-6">Start exploring the marketplace to find study materials</p>
              <Button
                data-testid="browse-docs-btn"
                onClick={() => navigate('/')}
                className="rounded-xl bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900"
              >
                Browse Documents
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Purchases;
